// Fonte de verdade das matriculas do servidor. Os dados vem do BACKEND real
// (GET /v1/servidores/me/matriculas) — os MESMOS que os outros perfis veem —
// e ficam num cache local (localStorage) para leitura sincrona pelas telas.
// A hidratacao acontece no login e ao entrar no app do servidor.

import type { MargemResponse } from "@atlas/types";
import type { ConsignacaoFacultativaDoServidor, DadosInformadosDoContrato } from "@atlas/sdk";
import { apiBaseUrl } from "./sdk";

export interface ContratoMock {
  id: string;
  banco: string;
  parcela: number;
  parcelasPagas: number;
  total: number;
  cronogramaPendente?: boolean;
  parcelasRestantes?: number | null;
  saldoDevedorConhecido?: number | null;
  /** `Substituído` (portado) e `Encerrado` (a folha parou de descontar) sao os
   *  fins de um legado; junto com `Quitado`, tiram o contrato dos ativos —
   *  ver `contratoServidorEncerrado`. */
  status: "Averbado" | "Em dia" | "Quitado" | "Substituído" | "Encerrado";
  proximaParcela: string;
  taxaAm: number;
  valorFinanciado: number;
  /** `legado` = ja estava na folha quando a Atlas chegou (ADR-0003). */
  origem?: "atlas" | "legado";
  /** `false` = legado sem informe: `valorFinanciado` e `taxaAm` nao valem e o
   *  card mostra `—`, nunca R$ 0,00. */
  dadosFinanceirosConhecidos?: boolean;
  /** Feature 012: quem informou principal e taxa do legado, e quando. */
  dadosInformados?: DadosInformadosDoContrato | null;
  pdfUrl: string;
  /** Tipo do contrato no backend: EMPRESTIMO | REFIN | ECONSIGNADO.
   *  Usado pelo card em /servidor/contratos pra rotular o produto correto. */
  tipoContrato?: "EMPRESTIMO" | "REFIN" | "ECONSIGNADO";
  /** Bucket de margem: distingue Cartao Consignado (CARTAO_CONSIGNADO) de
   *  Cartao Beneficio (CARTAO_BENEFICIOS) — ambos tem tipoContrato=ECONSIGNADO. */
  tipoMargem?: "EMPRESTIMO" | "CARTAO_CONSIGNADO" | "CARTAO_BENEFICIOS";
  /** Nome do arquivo do CCB anexado pelo banco (derivado do ccbKey no backend).
   *  null/ausente = nenhum contrato anexado -> nao mostra o botao "Baixar Contrato". */
  anexoNome?: string | null;
}


export interface MatriculaInfo {
  idMatricula: string;
  matricula: string;
  prefeitura: string;
  prefeitura_id: number;
  servidor_id: number;
  uf: string;
  cargo: string;
  vinculo: "ESTATUTARIO" | "CLT" | "COMISSIONADO";
  nome: string;
  /** CPF mascarado (ex: "580.***.***-53"). CPF completo NUNCA e retornado ao frontend. */
  cpfMasked: string;
  email: string;
  telefone: string;
  endereco: string;
  ativa: boolean;
  /** Flag da prefeitura — se true, servidor pode editar email/telefone em /conta. */
  permiteServidorEditarContato?: boolean;
  /** Se true, servidor precisa digitar senha ao clicar "Solicitar agora" no
   *  simulador. Vem da prefeitura via /prefeitura/config. */
  exigeSenhaProposta?: boolean;
  /** OU das duas flags da prefeitura — use ESTA pra decidir se mostra o campo
   *  de senha. `exigeSenhaProposta` sozinha escondia o campo em operacao que a
   *  API ia recusar. Ver exigirSenhaDeTransacao no backend. */
  exigeSenhaTransacao?: boolean;
  /** Se true, a prefeitura exige senha (proposta ou averbacao) E o servidor
   *  AINDA nao definiu a senha de transacao — a PENDENCIA. Frontend usa pra
   *  marcar o selo "Pendente". Cliente 30/07/2026. */
  precisaSenhaTransacao?: boolean;
  /** ISO 8601 de quando o servidor ATIVOU a conta pelo fluxo de primeiro-
   *  acesso (definiu senha). Cutoff pra filtrar notificacoes de ofertas
   *  antigas: ofertas com criadoEm < contaAtivadaEm nao aparecem no sino.
   *  Null pra quem ja tinha cadastro antes — esses veem todas as ofertas.
   *  Cliente 06/08/2026 (corrige a regra de 03/08, que usava o primeiro
   *  LOGIN e escondia ofertas de servidor antigo). */
  contaAtivadaEm?: string | null;
  /** Texto livre com exclusividades do cartão consignado dessa prefeitura. */
  exclusividadesCartaoConsig?: string;
  margem: MargemResponse;
  contratos: ContratoMock[];
  /**
   * Plano de saude, odonto, seguro, mensalidade (#276). Opcional: o cache local
   * pode ser de antes do campo existir. `null` = a API nao conseguiu ler.
   */
  consignacoesFacultativas?: ConsignacaoFacultativaDoServidor[] | null;
  /**
   * O orgao/entidade tem consignataria operando beneficio no portal (pelo
   * vinculo da rubrica), ou o servidor ja' tem beneficio descontando. Decide
   * se "Meus beneficios" aparece no menu. Opcional pelo mesmo motivo acima.
   */
  ofereceBeneficios?: boolean;
}

/**
 * "Meus beneficios" aparece? A API decide (`ofereceBeneficios`); com cache
 * de antes do campo, vale ter beneficio descontando — quem tem desconto nunca
 * perde o caminho para pedir o cancelamento.
 */
export function mostraMeusBeneficios(m: MatriculaInfo | null): boolean {
  if (!m) return false;
  if (typeof m.ofereceBeneficios === "boolean") return m.ofereceBeneficios;
  return (m.consignacoesFacultativas?.length ?? 0) > 0;
}

export const STORAGE_KEY_ID = "atlas:idMatricula";
export const STORAGE_KEY_META = "atlas:idMatricula:meta";
export const STORAGE_KEY_LIST = "atlas:matriculas";

function loadCachedMatriculas(): MatriculaInfo[] {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY_LIST) : null;
    return raw ? (JSON.parse(raw) as MatriculaInfo[]) : [];
  } catch {
    return [];
  }
}

/**
 * Matriculas do servidor logado. Cache populado do backend (fonte de verdade) e
 * persistido no localStorage para leitura sincrona apos reload. A referencia do
 * array e mantida (mutada no lugar) para que os imports existentes vejam a atualizacao.
 */
export const MATRICULAS: MatriculaInfo[] = loadCachedMatriculas();

/** Busca as matriculas reais no backend e atualiza o cache no lugar. Idempotente. */
export async function hydrateMatriculas(): Promise<MatriculaInfo[]> {
  try {
    if (typeof window === "undefined") return MATRICULAS;
    const tk = window.localStorage.getItem("atlas:tokens");
    const token = tk ? (JSON.parse(tk) as { access_token?: string }).access_token : null;
    if (!token) return MATRICULAS;
    const res = await fetch(`${apiBaseUrl}/v1/servidores/me/matriculas`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return MATRICULAS;
    const j = (await res.json()) as { matriculas: MatriculaInfo[] };
    MATRICULAS.length = 0;
    MATRICULAS.push(...(j.matriculas ?? []));
    window.localStorage.setItem(STORAGE_KEY_LIST, JSON.stringify(MATRICULAS));
    // Sinaliza as telas (que escutam 'storage' de STORAGE_KEY_ID/META) para re-ler.
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY_META }));
    return MATRICULAS;
  } catch {
    return MATRICULAS;
  }
}

export function getMatricula(idMatricula: string | null): MatriculaInfo | null {
  if (!idMatricula) return null;
  return MATRICULAS.find((m) => m.idMatricula === idMatricula) ?? null;
}

export function readActiveIdMatricula(): string | null {
  try {
    // Prefere ID direto. Se nao houver, tenta extrair do meta-snapshot legado.
    const direct = window.localStorage.getItem(STORAGE_KEY_ID);
    if (direct) return direct;
    const meta = window.localStorage.getItem(STORAGE_KEY_META);
    if (!meta) return null;
    return (JSON.parse(meta) as { idMatricula?: string }).idMatricula ?? null;
  } catch {
    return null;
  }
}

/**
 * Matricula ativa. Se o id guardado NAO estiver na lista atual, cai na primeira
 * — que e' a que o resto da tela ja esta exibindo.
 *
 * O id fica em localStorage e a lista e' recarregada a cada login; quando o
 * usuario do navegador troca (servidor sai e outro entra, ou a averbadora pula
 * de um servidor pra outro no modo impersonate), sobra um id apontando pra
 * matricula que nao existe mais na lista. Antes isso devolvia null e quem
 * dependia daqui simplesmente sumia: foi o que escondeu o cartao de senha de
 * transacao de um servidor cuja prefeitura EXIGE a senha — ele ficaria sem
 * como cadastra-la. Cliente 07/08/2026.
 */
export function readActiveMatricula(): MatriculaInfo | null {
  return getMatricula(readActiveIdMatricula()) ?? MATRICULAS[0] ?? null;
}

export function setActiveMatricula(idMatricula: string): void {
  const m = getMatricula(idMatricula);
  if (!m) return;
  window.localStorage.setItem(STORAGE_KEY_ID, idMatricula);
  // Continua escrevendo o snapshot pra retrocompatibilidade com codigo que le META_KEY.
  const snapshot = {
    idMatricula: m.idMatricula,
    matricula: m.matricula,
    prefeitura: m.prefeitura,
    uf: m.uf,
    cargo: m.cargo,
    vinculo: m.vinculo,
    nome: m.nome,
    email: m.email,
    telefone: m.telefone,
    endereco: m.endereco,
  };
  window.localStorage.setItem(STORAGE_KEY_META, JSON.stringify(snapshot));
}
